
import { useState } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';

export default function HomeValuationPage() {
  const [formData, setFormData] = useState({
    address: '',
    city: '',
    zipCode: '',
    bedrooms: '',
    bathrooms: '',
    squareFootage: '',
    yearBuilt: '',
    propertyType: 'single-family',
    condition: 'good',
    recentUpdates: [] as string[],
    email: '',
    phone: ''
  });

  const [valuation, setValuation] = useState<{
    estimatedValue: number;
    lowRange: number;
    highRange: number;
    pricePerSqFt: number;
  } | null>(null);

  const [showResults, setShowResults] = useState(false);

  const recentUpdateOptions = [
    'Kitchen Renovation',
    'Bathroom Renovation',
    'New Flooring',
    'Fresh Paint',
    'New Roof',
    'HVAC System',
    'Windows Replacement',
    'Landscaping',
    'Deck/Patio Addition',
    'Basement Finishing'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUpdateToggle = (update: string) => {
    setFormData(prev => ({
      ...prev,
      recentUpdates: prev.recentUpdates.includes(update)
        ? prev.recentUpdates.filter(u => u !== update)
        : [...prev.recentUpdates, update]
    }));
  };

  const calculateValuation = () => {
    const sqft = parseInt(formData.squareFootage) || 1500;
    const bedrooms = parseInt(formData.bedrooms) || 3;
    const bathrooms = parseFloat(formData.bathrooms) || 2;
    const yearBuilt = parseInt(formData.yearBuilt) || 1990;
    
    // Base price per sq ft for Metro Detroit
    let basePricePerSqFt = 145;
    
    // Adjust for property type
    if (formData.propertyType === 'condo') basePricePerSqFt *= 0.85;
    if (formData.propertyType === 'townhouse') basePricePerSqFt *= 0.95;
    if (formData.propertyType === 'luxury') basePricePerSqFt *= 1.4;
    
    // Adjust for age
    const age = 2025 - yearBuilt;
    if (age < 10) basePricePerSqFt *= 1.15;
    else if (age < 20) basePricePerSqFt *= 1.05;
    else if (age > 50) basePricePerSqFt *= 0.9;
    
    // Adjust for condition
    if (formData.condition === 'excellent') basePricePerSqFt *= 1.1;
    else if (formData.condition === 'fair') basePricePerSqFt *= 0.9;
    else if (formData.condition === 'poor') basePricePerSqFt *= 0.75;
    
    // Adjust for recent updates
    const updateBonus = formData.recentUpdates.length * 0.02;
    basePricePerSqFt *= (1 + updateBonus);
    
    // Calculate base value
    const baseValue = sqft * basePricePerSqFt;
    
    // Add bedroom/bathroom premiums
    const bedroomBonus = Math.max(0, bedrooms - 3) * 8000;
    const bathroomBonus = Math.max(0, bathrooms - 2) * 5000;
    
    const estimatedValue = Math.round(baseValue + bedroomBonus + bathroomBonus);
    const lowRange = Math.round(estimatedValue * 0.9);
    const highRange = Math.round(estimatedValue * 1.1);
    
    setValuation({
      estimatedValue,
      lowRange,
      highRange,
      pricePerSqFt: Math.round(basePricePerSqFt)
    });
    
    setShowResults(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    calculateValuation();
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <div className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-black mb-4">
              Free Home Valuation Tool
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Get an instant estimate of your home's current market value in Metro Detroit. Our advanced algorithm considers recent sales, market trends, and property features.
            </p>
          </div>

          {!showResults ? (
            <div className="bg-white border-2 border-yellow-500 rounded-lg p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-black mb-2">
                      Property Address *
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm"
                      placeholder="123 Main Street"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-black mb-2">
                      City *
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm"
                      placeholder="Detroit"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-black mb-2">
                      ZIP Code *
                    </label>
                    <input
                      type="text"
                      name="zipCode"
                      value={formData.zipCode}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm"
                      placeholder="48201"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-black mb-2">
                      Bedrooms *
                    </label>
                    <select
                      name="bedrooms"
                      value={formData.bedrooms}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm pr-8"
                    >
                      <option value="">Select</option>
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                      <option value="5">5+</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-black mb-2">
                      Bathrooms *
                    </label>
                    <select
                      name="bathrooms"
                      value={formData.bathrooms}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm pr-8"
                    >
                      <option value="">Select</option>
                      <option value="1">1</option>
                      <option value="1.5">1.5</option>
                      <option value="2">2</option>
                      <option value="2.5">2.5</option>
                      <option value="3">3</option>
                      <option value="3.5">3.5</option>
                      <option value="4">4+</option>
                    </select>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-black mb-2">
                      Square Footage *
                    </label>
                    <input
                      type="number"
                      name="squareFootage"
                      value={formData.squareFootage}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm"
                      placeholder="1500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-black mb-2">
                      Year Built *
                    </label>
                    <input
                      type="number"
                      name="yearBuilt"
                      value={formData.yearBuilt}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm"
                      placeholder="1990"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-black mb-2">
                      Property Type
                    </label>
                    <select
                      name="propertyType"
                      value={formData.propertyType}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm pr-8"
                    >
                      <option value="single-family">Single Family Home</option>
                      <option value="condo">Condominium</option>
                      <option value="townhouse">Townhouse</option>
                      <option value="luxury">Luxury Home</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-black mb-2">
                      Overall Condition
                    </label>
                    <select
                      name="condition"
                      value={formData.condition}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm pr-8"
                    >
                      <option value="excellent">Excellent</option>
                      <option value="good">Good</option>
                      <option value="fair">Fair</option>
                      <option value="poor">Needs Work</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-black mb-3">
                    Recent Updates/Improvements (Select all that apply)
                  </label>
                  <div className="grid md:grid-cols-2 gap-3">
                    {recentUpdateOptions.map((update) => (
                      <label key={update} className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.recentUpdates.includes(update)}
                          onChange={() => handleUpdateToggle(update)}
                          className="mr-3 h-4 w-4 text-yellow-500 focus:ring-yellow-500 border-gray-300 rounded"
                        />
                        <span className="text-sm text-gray-700">{update}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-black mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm"
                      placeholder="your@email.com"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-black mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm"
                      placeholder="(248) 123-4567"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full bg-yellow-500 text-black px-8 py-4 rounded-lg hover:bg-yellow-400 transition-colors font-semibold text-lg cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-calculator-line mr-2"></i>
                  Get My Home Valuation
                </button>
              </form>
            </div>
          ) : (
            <div className="space-y-8">
              <div className="bg-black text-white rounded-lg p-8 text-center">
                <h2 className="text-3xl font-bold mb-4">Your Home Valuation Results</h2>
                <div className="text-5xl font-bold text-yellow-500 mb-2">
                  ${valuation?.estimatedValue.toLocaleString()}
                </div>
                <p className="text-xl text-gray-300 mb-6">Estimated Market Value</p>
                
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-white text-black rounded-lg p-4">
                    <div className="text-2xl font-bold text-yellow-600">
                      ${valuation?.lowRange.toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-600">Low Range</div>
                  </div>
                  <div className="bg-white text-black rounded-lg p-4">
                    <div className="text-2xl font-bold text-yellow-600">
                      ${valuation?.highRange.toLocaleString()}
                    </div>
                    <div className="text-sm text-gray-600">High Range</div>
                  </div>
                  <div className="bg-white text-black rounded-lg p-4">
                    <div className="text-2xl font-bold text-yellow-600">
                      ${valuation?.pricePerSqFt}
                    </div>
                    <div className="text-sm text-gray-600">Price per Sq Ft</div>
                  </div>
                </div>
                
                <p className="text-gray-300 mb-6">
                  This estimate is based on recent comparable sales, current market conditions, and your property details. For a comprehensive market analysis, contact me for a personalized consultation.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button 
                    onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                    className="bg-yellow-500 text-black px-8 py-3 rounded-lg hover:bg-yellow-400 transition-colors font-semibold cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-calendar-line mr-2"></i>
                    Schedule CMA Consultation
                  </button>
                  <button 
                    onClick={() => setShowResults(false)}
                    className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-lg hover:bg-white hover:text-black transition-colors font-semibold cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-refresh-line mr-2"></i>
                    Calculate Another Property
                  </button>
                </div>
              </div>
              
              <div className="bg-white border-2 border-yellow-500 rounded-lg p-8">
                <h3 className="text-2xl font-bold text-black mb-6">Next Steps to Maximize Your Home's Value</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <i className="ri-check-line text-yellow-500 text-xl mt-1"></i>
                      <div>
                        <h4 className="font-semibold text-black">Professional Market Analysis</h4>
                        <p className="text-gray-600 text-sm">Get a detailed CMA with recent comparable sales and market trends.</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <i className="ri-check-line text-yellow-500 text-xl mt-1"></i>
                      <div>
                        <h4 className="font-semibold text-black">Home Staging Consultation</h4>
                        <p className="text-gray-600 text-sm">Professional tips to present your home in the best light.</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <i className="ri-check-line text-yellow-500 text-xl mt-1"></i>
                      <div>
                        <h4 className="font-semibold text-black">Strategic Pricing</h4>
                        <p className="text-gray-600 text-sm">Price your home competitively to attract qualified buyers.</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <i className="ri-check-line text-yellow-500 text-xl mt-1"></i>
                      <div>
                        <h4 className="font-semibold text-black">Marketing Strategy</h4>
                        <p className="text-gray-600 text-sm">Comprehensive marketing plan to reach qualified buyers.</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <i className="ri-check-line text-yellow-500 text-xl mt-1"></i>
                      <div>
                        <h4 className="font-semibold text-black">Negotiation Support</h4>
                        <p className="text-gray-600 text-sm">Expert negotiation to get you the best possible price.</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <i className="ri-check-line text-yellow-500 text-xl mt-1"></i>
                      <div>
                        <h4 className="font-semibold text-black">Closing Coordination</h4>
                        <p className="text-gray-600 text-sm">Smooth transaction management from contract to closing.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <Footer />
    </div>
  );
}